import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useGetBonusCampaignQuery,
  useLazyGetDisbursementsQuery,
  usePayBonusMutation
} from '../../../../api/bonusCampaign';
import Table from '../../../../components/shared/Table';
import moment from 'moment';
import { handleStatusesBadge } from '../../../../constants/extras/statusesBadge';
import ConfirmDialog from '../../../../components/shared/customModal/confirmDialog';
import { checkForError } from '../../../../constants/extras/errorHandlers';
import { sendSuccessMessage } from '../../../../constants/extras/sendSuccessMsg';
import HeaderTextLayout from '../../../../components/shared/HeaderTextLayout';
import InfiniteScroll from 'react-infinite-scroll-component';
import { CircularProgress, Fade, Menu, MenuItem } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { IoSearchSharp } from 'react-icons/io5';
import BackspaceIcon from '@mui/icons-material/Backspace';
import {
  createDate,
  DATE,
  DateCreater
} from '../../../../constants/variables/dates';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { DateRangePicker } from 'react-date-range';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import { exportMenu } from '../../../../constants/variables/arrays';
import { CSVLink } from 'react-csv';
import { actions } from '../../../../redux/store/store';
import CancelIcon from '@mui/icons-material/Cancel';
import { Refresh } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import {
  dataExport,
  xlsxDataExport
} from '../../../../constants/extras/dataExport';
import {
  BonusHeaders,
  formatBonusTransactions
} from '../../../../constants/csv/csvBonusTransaction';
import { useGetMetrosQuery } from '../../../../api/metro';
import { multiSelectStyle } from '../../../../constants/multiSelectDropdown/multiSelectStyle';
import MultiSelect from '../../../../constants/multiSelectDropdown';
import { ReactComponent as DoneLogo } from '../../../../images/logo-done.svg';
import ReactSelect from 'react-select';
import {
  TransactionStatus,
  CampaignType
} from '../../../../constants/menuLists';

const pageLimit = 100;

export default function BonusDisbursement() {
  const [getTransactionsFn, { isFetching }] = useLazyGetDisbursementsQuery();
  const [payAgain, { isLoading: payLoading }] = usePayBonusMutation();
  const { data: metros } = useGetMetrosQuery(null, {
    refetchOnMountOrArgChange: true
  });

  const { data: camps } = useGetBonusCampaignQuery();
  const CampaignList = useMemo(
    () => camps?.map((c) => ({ label: c?.name, value: c?._id })) || [],
    [camps]
  );

  const isRefetch = useSelector((state) => state.refetch.value);

  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [metro, setMetro] = useState([]);
  const [status, setStatus] = useState('');
  const [campaign, setCampaign] = useState('');
  const [type, setType] = useState('');
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [fromDate, setFromDate] = useState(
    new DateCreater(new Date(new Date().setDate(new Date().getDate() - 14))).dt
  );
  const [toDate, setToDate] = useState(new DateCreater(new Date()).dt);
  const [state, setState] = useState([
    {
      startDate: new Date(new Date().setDate(new Date().getDate() - 14)),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [prevDate, setPrevDate] = useState(state);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [csvData, setCsvData] = useState(false);
  const [loadingCsv, setLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState([]);

  const wrapperRef = useRef(null);
  const csvInstance = useRef();

  const fetchTransactions = async (param = {}) => {
    const { refetch, clearSearch } = param;

    const result = await getTransactionsFn({
      page: refetch ? 1 : page,
      limit: pageLimit,
      search: clearSearch ? '' : searchQuery,
      status: status?.value || '',
      type: type?.value || '',
      campaign: campaign?.value || '',
      fromDate,
      toDate,
      metros: JSON.stringify(metro?.map((item) => item.value))
    }).unwrap();

    setTotal(result.count);
    setHasMore(result.data.length === pageLimit);
    setCount((c) => (refetch ? result.data.length : c + result.data.length));

    const groupedTransactions = [];
    const referralMap = new Map();

    result.data.forEach((transaction) => {
      if (transaction.referralId) {
        if (!referralMap.has(transaction.referralId)) {
          referralMap.set(transaction.referralId, {
            ...transaction,
            driverName: `${transaction.driverName} (${transaction.referralType})`,
            groupedDrivers: [
              {
                driverName: transaction.driverName,
                referralType: transaction.referralType,
                metro: transaction.metro,
                status: transaction.status,
                timestamp: transaction.timestamp,
                amount: transaction.amount
              }
            ]
          });
        } else {
          const existingGroup = referralMap.get(transaction.referralId);
          existingGroup.groupedDrivers.push({
            driverName: transaction.driverName,
            referralType: transaction.referralType,
            metro: transaction.metro,
            status: transaction.status,
            timestamp: transaction.timestamp,
            amount: transaction.amount
          });
        }
      } else {
        groupedTransactions.push(transaction);
      }
    });

    groupedTransactions.push(...Array.from(referralMap.values()));

    groupedTransactions.sort((a, b) => b.timestamp - a.timestamp);

    if (refetch) setTransactions(groupedTransactions);
    else setTransactions((prev) => [...prev, ...groupedTransactions]);
  };

  useEffect(() => {
    fetchTransactions();
  }, [page]);

  useEffect(() => {
    if (!isRefetch) return;
    handleFilter();
    actions.refetch.setIsDataRefetch(false);
  }, [isRefetch]);

  const handleFilter = (params = {}) => {
    const { clearSearch } = params;
    setTransactions([]);
    setTotal(0);
    setCount(0);
    setHasMore(true);

    fetchTransactions({ refetch: true, clearSearch });
  };

  const onCancelClick = () => {
    setShowDateRangePicker(false);
    setState(prevDate);
  };

  const onResetClick = async () => {
    setShowDateRangePicker(false);
    const currDt = [
      {
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection'
      }
    ];
    const { dt: strtDt } = new DateCreater(new Date());
    const { dt: endDt } = new DateCreater(new Date());
    setFromDate(strtDt);
    setToDate(endDt);
    setState(currDt);
    setPrevDate(currDt);
    actions.refetch.setIsDataRefetch(true);
  };

  const handleTimeRangeSelection = async () => {
    const { startDate, endDate } = state[0] || {};
    const { dt: strtDt } = new DateCreater(startDate);
    const { dt: endDt } = new DateCreater(endDate);
    setFromDate(strtDt);
    setToDate(endDt);
    setShowDateRangePicker(false);
    setPrevDate(state);
    actions.refetch.setIsDataRefetch(true);
  };

  const exportData = async (value) => {
    setAnchorEl(null);
    setLoading(true);

    const result = await getTransactionsFn({
      search: searchQuery,
      status: status?.value || '',
      type: type?.value || '',
      campaign: campaign?.value || '',
      fromDate,
      toDate,
      metros: JSON.stringify(metro?.map((item) => item.value))
    }).unwrap();

    setLoading(false);

    const filename = 'BonusTransaction.csv';
    const data = formatBonusTransactions(result?.data || []);
    if (value === 'csv') {
      const obj = dataExport(data, filename, BonusHeaders);
      setCsvData(obj);
    } else if (value === 'xlsx') {
      xlsxDataExport(data, 'BonusTransaction');
    }
  };

  useEffect(() => {
    if (csvData && csvInstance.current && csvInstance.current.link) {
      setTimeout(() => {
        csvInstance.current.link.click();
        setCsvData(false);
      });
    }
  }, [csvData]);

  const handleSelectAll = (e) => {
    e.target.checked
      ? setSelectedTransaction(
          transactions?.filter((t) => t.status === 'FAILED')
        )
      : setSelectedTransaction([]);
  };

  const handleSelect = (e, id) => {
    e.target.checked
      ? setSelectedTransaction((prev) => [...prev, id])
      : setSelectedTransaction(selectedTransaction.filter((t) => t !== id));
  };

  const columns = useMemo(
    () => [
      ...(transactions?.some((t) => t.status === 'FAILED')
        ? [
            {
              name: 'checkbox',
              label: (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '20px'
                  }}
                >
                  <input
                    type="checkbox"
                    onClick={handleSelectAll}
                    checked={
                      selectedTransaction?.length ===
                      transactions?.filter((t) => t.status === 'FAILED')?.length
                    }
                    onChange={() => {}}
                    className="mainCheckbox"
                  />
                </div>
              ),
              className: 'table__td_has-checkbox',
              renderer: (item) => {
                const isSelected = (rec) =>
                  selectedTransaction.indexOf(rec) !== -1;
                const isItemSelected = isSelected(item);
                return (
                  <div className="orders-input-checkbox">
                    {item?.status === 'FAILED' && (
                      <input
                        type="checkbox"
                        onClick={(e) => handleSelect(e, item._id)}
                        checked={isItemSelected}
                        onChange={() => {}}
                      />
                    )}
                  </div>
                );
              }
            }
          ]
        : []),
      {
        name: 'campaign',
        label: 'Campaign',
        sortable: true,
        renderer: ({ campaign }) => campaign?.name
      },
      {
        name: 'campaign',
        label: 'Type',
        sortable: true,
        renderer: ({ campaign }) => campaign?.type
      },
      {
        name: 'driver',
        label: 'Driver Name',
        sortable: true,
        renderer: ({ driverName, groupedDrivers }) => (
          <div>
            {groupedDrivers
              ? groupedDrivers.map((driver, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '3px 0',
                      borderBottom:
                        index !== groupedDrivers.length - 1
                          ? '1px solid #e0e0e0'
                          : 'none'
                    }}
                  >
                    {driver.driverName}{' '}
                    {driver.referralType ? `(${driver.referralType})` : ''}
                  </div>
                ))
              : driverName}
          </div>
        )
      },
      {
        name: 'metro',
        label: 'Metro',
        sortable: true,
        renderer: ({ groupedDrivers, metro }) => (
          <div>
            {groupedDrivers
              ? groupedDrivers.map((driver, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '3px 0',
                      borderBottom:
                        index !== groupedDrivers.length - 1
                          ? '1px solid #e0e0e0'
                          : 'none'
                    }}
                  >
                    {driver.metro}
                  </div>
                ))
              : metro}
          </div>
        )
      },
      {
        name: 'amount',
        label: 'Amount',
        sortable: true,
        renderer: ({ groupedDrivers, amount }) =>
          groupedDrivers
            ? groupedDrivers.map((driver, index) => (
                <div
                  key={index}
                  style={{
                    padding: '3px 0',
                    borderBottom:
                      index !== groupedDrivers.length - 1
                        ? '1px solid #e0e0e0'
                        : 'none'
                  }}
                >
                  {'$' + driver?.amount?.toFixed(2)}
                </div>
              ))
            : '$' + amount?.toFixed(2)
      },
      {
        name: 'status',
        label: 'Status',
        sortable: true,
        renderer: ({ groupedDrivers, status }) =>
          groupedDrivers ? (
            <div>
              {groupedDrivers.map((driver, index) => (
                <div
                  key={index}
                  style={{
                    padding: '4px 0',
                    borderBottom:
                      index !== groupedDrivers.length - 1
                        ? '1px solid #e0e0e0'
                        : 'none'
                  }}
                >
                  <span style={handleStatusesBadge(driver.status)}>
                    {driver.status}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: '4px 0' }}>
              <span style={handleStatusesBadge(status)}>{status}</span>
            </div>
          )
      },
      ...(transactions?.some((t) => t.status === 'FAILED')
        ? [
            {
              name: 'statusCode',
              label: 'Reason',
              sortable: true
            }
          ]
        : []),
      {
        name: 'timestamp',
        label: 'Date',
        sortable: true,
        renderer: ({ groupedDrivers, timestamp }) => (
          <div>
            {groupedDrivers
              ? groupedDrivers.map((driver, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '3px 0',
                      borderBottom:
                        index !== groupedDrivers.length - 1
                          ? '1px solid #e0e0e0'
                          : 'none'
                    }}
                  >
                    {moment(driver.timestamp).format('MM-DD-YYYY hh:mm A')}
                  </div>
                ))
              : moment(timestamp).format('MM-DD-YYYY hh:mm A')}
          </div>
        )
      },
      ...(transactions?.some((t) => t.status === 'FAILED')
        ? [
            {
              renderer: (item) => (
                <button
                  className="btn btn_block btn_accent customized-details-btn"
                  type="button"
                  onClick={() => {
                    setPayRecord([true, item]);
                  }}
                >
                  Pay
                </button>
              )
            }
          ]
        : [])
    ],
    [selectedTransaction, transactions]
  );

  const [payRecord, setPayRecord] = useState([false, null]);

  const handleAction = async () => {
    const body = payRecord[1]?._id ? [payRecord[1]?._id] : selectedTransaction;
    const result = await payAgain({ body: body });
    if (!result?.error) {
      sendSuccessMessage('Tried Successfully');
    } else {
      checkForError(result);
    }
    payRecord[1]?._id ? setPayRecord([false, '']) : setSelectedTransaction([]);
  };

  return (
    <>
      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="card">
              <div
                style={{
                  display: 'flex',
                  padding: '10px',
                  zIndex: 999
                }}
              >
                <div
                  style={{
                    width: '250px',
                    position: 'relative'
                  }}
                >
                  <div style={{ width: '100%', padding: '20px' }}>
                    <h3
                      className={`card-title header__text ${
                        count <= 0 || count === '0' ? 'mt-2' : ''
                      }`}
                    >
                      Disbursements List
                    </h3>
                    <p
                      style={{
                        position: 'absolute',
                        top: '45px',
                        color: '#4d4d4d',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}
                    >
                      {count > 0 &&
                        (count < 10
                          ? '0'.concat(
                              `${count} Disbursements (${total} Found)`
                            )
                          : `${count} Disbursements (${total} Found)`)}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'end'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flexWrap: 'wrap',
                      alignItems: 'center'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        flexWrap: 'wrap',
                        justifyContent: 'end',
                        width: '100%'
                      }}
                    >
                      <ReactSelect
                        options={CampaignList}
                        isClearable={true}
                        onChange={(item) => {
                          setCampaign(item);
                          actions.refetch.setIsDataRefetch(true);
                        }}
                        placeholder="Select Campaign"
                        value={campaign}
                        components={{
                          IndicatorSeparator: () => null
                        }}
                        styles={{
                          placeholder: (base) => ({
                            ...base,
                            fontSize: '13px',
                            fontWeight: 'normal'
                          }),
                          control: (styles) => ({
                            ...styles,
                            borderRadius: '5px',
                            boxShadow: 'none',
                            height: '2.8rem',
                            '&:hover': {
                              border: '2px solid #dadada'
                            },
                            border: '2px solid #dadada',
                            fontSize: '13px',
                            width: '185px'
                          })
                        }}
                      />
                      <ReactSelect
                        options={CampaignType?.map((item) => ({
                          label: item,
                          value: item
                        }))}
                        isClearable={true}
                        onChange={(item) => {
                          setType(item);
                          actions.refetch.setIsDataRefetch(true);
                        }}
                        placeholder="Select Type"
                        value={type}
                        components={{
                          IndicatorSeparator: () => null
                        }}
                        styles={{
                          placeholder: (base) => ({
                            ...base,
                            fontSize: '13px',
                            fontWeight: 'normal'
                          }),
                          control: (styles) => ({
                            ...styles,
                            borderRadius: '5px',
                            boxShadow: 'none',
                            height: '2.8rem',
                            '&:hover': {
                              border: '2px solid #dadada'
                            },
                            border: '2px solid #dadada',
                            fontSize: '13px',
                            width: '185px'
                          })
                        }}
                      />

                      <ReactSelect
                        options={TransactionStatus?.map((item) => {
                          return {
                            value: item.toUpperCase(),
                            label: item
                          };
                        })}
                        isClearable={true}
                        onChange={(item) => {
                          setStatus(item);
                          actions.refetch.setIsDataRefetch(true);
                        }}
                        placeholder="Select Status"
                        value={status}
                        components={{
                          IndicatorSeparator: () => null
                        }}
                        styles={{
                          placeholder: (base) => ({
                            ...base,
                            fontSize: '13px',
                            fontWeight: 'normal'
                          }),
                          control: (styles) => ({
                            ...styles,
                            borderRadius: '5px',
                            boxShadow: 'none',
                            height: '2.8rem',
                            '&:hover': {
                              border: '2px solid #dadada'
                            },
                            border: '2px solid #dadada',
                            fontSize: '13px',
                            width: '185px'
                          })
                        }}
                      />

                      <div className="d-flex justify-content-end status_filter">
                        <MultiSelect
                          placeholder="Select Metros"
                          value={metro}
                          itemData={metros?.map((item, i) => ({
                            value: item?.name ? item?.name : '-',
                            label: `${item?.name ? item?.name : ''}`
                          }))}
                          styles={{
                            ...multiSelectStyle(),
                            control: (styles) => ({
                              ...styles,
                              borderRadius: '5px',
                              boxShadow: 'none',
                              '&:hover': {
                                borderColor: '#dadada'
                              },
                              border: '2px solid #dadada !important',
                              borderRight: 'none !important',
                              borderTopRightRadius: '0 !important',
                              borderBottomRightRadius: '0 !important',
                              width: '200px',
                              height: '2.8rem',
                              overflowY: 'auto',
                              padding: 0
                            }),
                            menu: (styles) => ({
                              ...styles,
                              width: '250px'
                            })
                          }}
                          onChange={(value) => {
                            setMetro(value);
                            if (!value?.length)
                              actions.refetch.setIsDataRefetch(true);
                          }}
                        />
                        <button
                          disabled={metro?.length < 1}
                          className="search__button"
                          onClick={() => {
                            handleFilter();
                          }}
                        >
                          <DoneLogo fill="#5fb6f7" width="24px" height="24px" />
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        flexWrap: 'wrap',
                        justifyContent: 'end',
                        width: '100%',
                        marginTop: '9px'
                      }}
                    >
                      <div className="filter__ctrls-holder filter__input-holder d-flex justify-content-end search__container">
                        <div className="searchbar__container">
                          <input
                            className="form__input customized__input"
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              if (e.target.value === '') {
                                setSearchQuery('');
                                handleFilter({ clearSearch: true });
                              }
                            }}
                            placeholder="Quick Search"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleFilter();
                              }
                            }}
                            disabled={isFetching}
                          />
                          {searchQuery?.length > 0 && (
                            <ClearIcon
                              className="clear__btn filter__search-icon"
                              onClick={() => {
                                if (!isFetching && searchQuery?.length > 0) {
                                  setSearchQuery('');
                                  handleFilter({ clearSearch: true });
                                }
                              }}
                              style={{
                                right: '45px',
                                top: '12px'
                              }}
                            />
                          )}
                          <button
                            disabled={searchQuery?.length <= 0}
                            className="search__button"
                            onClick={() => handleFilter()}
                          >
                            <IoSearchSharp />
                          </button>
                        </div>
                      </div>

                      <div ref={wrapperRef} style={{ position: 'relative' }}>
                        <div
                          onClick={() => {
                            !isFetching &&
                              setShowDateRangePicker((prev) => !prev);
                            setState(prevDate);
                          }}
                          className="form__input "
                        >
                          <span>
                            {fromDate
                              ? `${createDate(
                                  fromDate,
                                  DATE.M_D_Y
                                )} TO ${createDate(toDate, DATE.M_D_Y)}`
                              : 'Select Custom Date Range'}
                          </span>
                          <span className="ml-2">
                            {showDateRangePicker ? (
                              <KeyboardArrowUpIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                          </span>
                        </div>
                        {showDateRangePicker && (
                          <div
                            className="mainDateRangePicker mt-3"
                            style={{
                              position: 'absolute',
                              top: '45px',
                              right: '-100px'
                            }}
                          >
                            <DateRangePicker
                              onChange={(item) => setState([item.selection])}
                              months={1}
                              ranges={state}
                            />
                            <div className="button-container">
                              <button
                                onClick={onResetClick}
                                className="okButton clearButton"
                              >
                                <span>
                                  <BackspaceIcon /> &nbsp;
                                  <strong>RESET</strong>
                                </span>
                              </button>
                              <button
                                onClick={onCancelClick}
                                className="cancelButton"
                              >
                                <span>
                                  <CancelIcon
                                    sx={{ width: '1.5rem', height: '1.5rem' }}
                                  />
                                  <strong>cancel</strong>
                                </span>
                              </button>
                              <button
                                onClick={handleTimeRangeSelection}
                                className="okButton"
                              >
                                <span>
                                  <ThumbUpAltIcon /> &nbsp;
                                  <strong>Done</strong>
                                </span>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <button
                          id="fade-button"
                          className="btn btn_block btn_accent btn_export_15"
                          onClick={(event) => setAnchorEl(event.currentTarget)}
                          variant="contained"
                          disabled={
                            loadingCsv || transactions?.length === 0
                              ? true
                              : false
                          }
                        >
                          {loadingCsv ? (
                            <div className="loading" style={{ color: 'white' }}>
                              <span
                                className="loading__text"
                                style={{ color: 'white' }}
                              >
                                Exporting
                              </span>
                              ...
                            </div>
                          ) : (
                            <span>
                              Export <KeyboardArrowDownIcon className="mr-0" />
                            </span>
                          )}
                        </button>
                        <Menu
                          id="fade-menu"
                          MenuListProps={{
                            'aria-labelledby': 'fade-button'
                          }}
                          anchorEl={anchorEl}
                          open={open}
                          onClick={() => setAnchorEl(null)}
                          TransitionComponent={Fade}
                        >
                          {exportMenu.map(({ value, name }) => (
                            <MenuItem
                              key={name}
                              value={name}
                              onClick={() => exportData(value)}
                              style={{ width: '100px' }}
                            >
                              {name}
                            </MenuItem>
                          ))}
                        </Menu>
                      </div>

                      {csvData ? (
                        <CSVLink
                          data={csvData.data}
                          headers={csvData.headers}
                          filename={csvData.filename}
                          ref={csvInstance}
                        />
                      ) : undefined}

                      <button
                        className="refresh_btn"
                        onClick={() => actions.refetch.setIsDataRefetch(true)}
                      >
                        <Refresh className="refresh_icon" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <InfiniteScroll
                className="infinit_table"
                dataLength={transactions?.length}
                next={() => {
                  if (!isFetching && hasMore) {
                    setPage((prev) => prev + 1);
                  }
                }}
                hasMore={hasMore}
                style={{
                  paddingBottom: !hasMore ? '0px' : '10px',
                  borderTop: '1px solid #dadada'
                }}
                loader={
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{
                      height:
                        transactions && transactions?.length <= 0
                          ? '70vh'
                          : 'auto'
                    }}
                  >
                    <CircularProgress className="loading-progress" />
                  </div>
                }
                height={'75vh'}
                endMessage={
                  transactions &&
                  transactions?.length <= 0 && (
                    <div
                      style={{
                        height: '70vh'
                      }}
                      className="d-flex justify-content-center align-items-center"
                    >
                      <p>
                        <b>No Result Found</b>
                      </p>
                    </div>
                  )
                }
              >
                {isFetching && !transactions.length ? (
                  <div className="loading-holder">
                    <CircularProgress className="loading-progress" />
                  </div>
                ) : (
                  <Table
                    columns={columns}
                    items={transactions}
                    className="table_drivers"
                    width={'100%'}
                  />
                )}
              </InfiniteScroll>
            </div>
          </div>
        </div>
      </div>
      {(payRecord[0] || selectedTransaction?.length > 0) && (
        <ConfirmDialog
          title="Pay Transaction"
          bodyText="Are you sure you want to pay this transaction?"
          isOpen
          isFetching={payLoading}
          onClose={() => {
            payRecord[0]
              ? setPayRecord([false, ''])
              : setSelectedTransaction([]);
          }}
          confirmAction={() => {
            handleAction();
          }}
        />
      )}
    </>
  );
}
